import { useEffect } from 'react';

// Single source of truth for the production origin. Keep this in sync with
// the canonical URLs in frontmatter and the sitemap generator.
export const SITE_URL = 'https://irelandvisatracker.in';
export const SITE_NAME = 'Irish Visa Tracker';

const stripQueryAndHash = (pathname) => {
  if (!pathname) return '/';
  return pathname.replace(/[?#].*$/, '') || '/';
};

// Build a canonical URL from the current pathname only — never include
// query strings, hashes, or user-specific parameters. Falls back to the
// provided `canonical` prop or the site root.
const buildCanonical = (canonical) => {
  if (canonical) {
    if (/^https?:\/\//i.test(canonical)) return canonical;
    return `${SITE_URL}${canonical.startsWith('/') ? canonical : `/${canonical}`}`;
  }
  if (typeof window === 'undefined') return SITE_URL + '/';
  const pathname = stripQueryAndHash(window.location.pathname);
  // Collapse trailing slashes (except for the root) so we don't fragment
  // canonical signals between `/foo` and `/foo/`.
  const normalized = pathname !== '/' && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return `${SITE_URL}${normalized}`;
};

const MetaTags = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  robots = 'index, follow',
  schema // Optional JSON-LD payload to inject for this route.
}) => {
  const defaultTitle = `${SITE_NAME} - Track Irish Visa Decisions & Get Instant Alerts`;
  const defaultDescription =
    'Get instant email + web push alerts when your Ireland visa status changes, plus access to all 13 premium tools. Track daily visa decisions, view live trends, and stay updated on your application progress.';
  const defaultKeywords =
    'Ireland visa status India, check Ireland visa status, Ireland visa email alerts, Ireland visa web push alerts, track Ireland visa from India, Ireland visa tracker, visa decision alerts, Ireland immigration, NDVO visa, AVATS Ireland visa status, VFS Ireland visa tracking, Ireland visa for Indians, visa processing time Ireland, premium visa tools';

  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalCanonical = buildCanonical(canonical);

  // Always reference the production origin for OG, even if the SPA happens
  // to be loaded on the secondary domain.
  const finalOgImage = ogImage
    ? /^https?:\/\//i.test(ogImage)
      ? ogImage
      : `${SITE_URL}${ogImage.startsWith('/') ? ogImage : `/${ogImage}`}`
    : `${SITE_URL}/images/og-image.svg`;

  useEffect(() => {
    document.title = finalTitle;

    const updateMetaTag = (name, content, property = false) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const updateLinkTag = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Basic meta tags
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);
    updateMetaTag('robots', robots);

    // Canonical URL — always the production origin + clean pathname
    updateLinkTag('canonical', finalCanonical);

    // Open Graph
    updateMetaTag('og:title', ogTitle || finalTitle, true);
    updateMetaTag('og:description', ogDescription || finalDescription, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', finalCanonical, true);
    updateMetaTag('og:site_name', SITE_NAME, true);
    updateMetaTag('og:image', finalOgImage, true);

    // Twitter Card
    updateMetaTag('twitter:card', twitterCard);
    updateMetaTag('twitter:title', ogTitle || finalTitle);
    updateMetaTag('twitter:description', ogDescription || finalDescription);
    updateMetaTag('twitter:image', finalOgImage);

    // Inject route-specific JSON-LD, replacing any prior one we placed.
    if (schema) {
      // Remove any previous route-specific schema we previously added.
      document
        .querySelectorAll('script[data-ivt-route-schema="true"]')
        .forEach((node) => node.remove());
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-ivt-route-schema', 'true');
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    // Add a basic Author tag for E-E-A-T signaling.
    updateMetaTag('author', SITE_NAME);
  }, [
    finalTitle,
    finalDescription,
    finalKeywords,
    finalCanonical,
    finalOgImage,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    twitterCard,
    robots,
    schema
  ]);

  return null;
};

export default MetaTags;
